import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useToast } from 'src/shared/hooks/use-toast'

import { queryKeys } from '../db/queryKeys'
import { ServerRoutes } from './serverRoutes'

interface MerchGPTProps {
  chat_id?: number
  org_id?: string
}

/** Sync merch-gpt turns include search + CRUD + mockups + the agent. */
const MERCH_GPT_TIMEOUT_MS = 120_000

export const useMerchGPT = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const abortControllerRef = useRef<AbortController | null>(null)
  const timedOutRef = useRef(false)

  const mutation = useMutation({
    mutationFn: async (props: MerchGPTProps) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller
      timedOutRef.current = false
      const timeoutId = window.setTimeout(() => {
        timedOutRef.current = true
        // Abort this request's controller, not whatever currently sits on the ref
        // (a newer mutate may already own the ref).
        controller.abort()
      }, MERCH_GPT_TIMEOUT_MS)

      try {
        const response = await fetch(
          import.meta.env.VITE_BE_SERVER_URL + ServerRoutes.merchGPT,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              chat_id: props.chat_id,
              org_id: props.org_id,
            }),
            signal: controller.signal,
          }
        )

        if (!response.ok) {
          let message = 'Failed to generate merch'
          try {
            const errorData = (await response.json()) as { msg?: string }
            if (errorData?.msg) message = errorData.msg
          } catch {
            // Non-JSON error bodies still fail the turn.
          }
          throw new Error(message)
        }

        return await response.json()
      } finally {
        window.clearTimeout(timeoutId)
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.chats] })
      timedOutRef.current = false
    },
    onError: (error: Error) => {
      const timedOut = timedOutRef.current
      timedOutRef.current = false

      if (error.name === 'AbortError') {
        if (timedOut) {
          toast.toast({
            title: 'Sara is taking too long',
            description: 'Please try again in a moment.',
            variant: 'destructive',
            duration: 5000,
          })
        }
        return
      }
      toast.toast({
        title: 'Something went wrong',
        description: error.message,
        variant: 'destructive',
        duration: 5000,
      })
    },
  })

  const stop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      mutation.reset()
    }
  }

  return {
    ...mutation,
    stop,
  }
}
